import { Controller } from "@hotwired/stimulus"
import { ArcadeStage } from "../utils/arcade_stage"

// Memory des espaces : seize tuiles, huit sociétés en double, avec leur vrai logo quand
// elles en ont un (cf. Arcade::MemoryBoard).
//
// Le score part d'un capital et fond à chaque paire ratée : sans ça, tout le monde finirait
// par retourner les seize tuiles et tout le monde aurait le même score.
const PENALTY = 15
const START_SCORE = 240
const FLIP_BACK_MS = 800

export default class extends Controller {
  static values = { labels: Object, url: String, scoresUrl: String }

  start() {
    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
    })
    this.stage.open()
    this.board = document.createElement("div")
    this.board.className = "arcade-memory"
    this.stage.board.appendChild(this.board)
    this.#play()
  }

  #stop() {
    window.clearTimeout(this.timer)
    this.request?.abort()
    this.request = null
    this.board = null
  }

  async #play() {
    this.#message(this.labelsValue.loading || "…")

    this.request?.abort()
    this.request = new AbortController()

    // Le `try` ne couvre QUE la requête (cf. la même remarque dans arcade_moles_controller).
    let tiles
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        signal: this.request.signal,
      })
      if (!response.ok) throw new Error(response.statusText)

      tiles = (await response.json()).tiles
    } catch (error) {
      if (error.name === "AbortError") return
      this.#message(this.labelsValue.error || "")
      return
    }

    if (!tiles) return this.#message(this.labelsValue.empty || this.labelsValue.error)

    this.#deal(tiles)
  }

  #message(text) {
    if (!this.board) return
    this.board.className = "arcade-memory arcade-memory--message"
    this.board.replaceChildren()
    const node = document.createElement("p")
    node.className = "arcade-quiz__message"
    node.textContent = text
    this.board.appendChild(node)
  }

  #deal(tiles) {
    if (!this.board) return

    this.pairsLeft = tiles.length
    this.mistakes = 0
    this.flipped = []
    this.locked = false
    this.stage.setScore(START_SCORE)
    this.stage.setHint(this.labelsValue.hint || "")

    this.board.className = "arcade-memory"
    this.board.replaceChildren()
    // Chaque société en double, puis on bat le paquet.
    const deck = [...tiles, ...tiles]
      .map((tile) => ({ tile, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ tile }) => tile)

    deck.forEach((tile) => this.board.appendChild(this.#card(tile)))
  }

  #card(tile) {
    const card = document.createElement("button")
    card.type = "button"
    card.className = "arcade-memory__card"
    card.dataset.id = String(tile.id)
    // Le nom est sur la carte dès le départ pour les lecteurs d'écran : cacher l'information
    // à l'accessibilité pour préserver un jeu de mémoire visuelle n'en vaut pas la peine.
    card.setAttribute("aria-label", tile.title)

    const face = document.createElement("span")
    face.className = "arcade-memory__face"

    if (tile.logo_url) {
      const image = new Image()
      image.alt = tile.title
      image.className = "arcade-memory__logo"
      // Logo introuvable : le nom de l'espace fait un dos de carte tout aussi jouable.
      image.addEventListener("error", () => { image.remove(); face.textContent = tile.title }, { once: true })
      face.appendChild(image)
      image.src = tile.logo_url
    } else {
      face.textContent = tile.title
      face.classList.add("arcade-memory__face--text")
    }

    card.appendChild(face)
    card.addEventListener("click", () => this.#flip(card))
    return card
  }

  #flip(card) {
    if (this.locked || card.classList.contains("arcade-memory__card--up")) return

    card.classList.add("arcade-memory__card--up")
    this.flipped.push(card)
    if (this.flipped.length < 2) return

    const [first, second] = this.flipped
    this.flipped = []

    if (first.dataset.id === second.dataset.id) return this.#pair(first, second)

    this.mistakes += 1
    this.stage.setScore(Math.max(0, START_SCORE - this.mistakes * PENALTY))
    this.locked = true
    this.timer = window.setTimeout(() => {
      first.classList.remove("arcade-memory__card--up")
      second.classList.remove("arcade-memory__card--up")
      this.locked = false
    }, FLIP_BACK_MS)
  }

  #pair(first, second) {
    first.classList.add("arcade-memory__card--matched")
    second.classList.add("arcade-memory__card--matched")
    first.disabled = true
    second.disabled = true

    this.pairsLeft -= 1
    if (this.pairsLeft > 0) return

    this.stage.finish({ score: this.stage.score, message: this.labelsValue.cleared || "" })
  }
}
